// Decisión común de los syncs del Checklist (incidentes, permisos, inspecciones,
// productos, puestos de trabajo, Matriz de Riesgos y GA-02). Bloque 6, D y
// decisión de JC del 23/10/2026: manda el módulo.
//   - el módulo cumple            → "cumple"
//   - tiene datos pero no cumple  → "en_proceso"
//   - no tiene ningún dato        → "pendiente"
// Un estado manual viejo se corrige en la próxima carga. Nunca se pisa el
// "No aplica" manual del administrador (también lo protege la base, M15).

export interface EstadoGuardado {
  item_id: string;
  estado: string;
  auto_source: string | null;
}

export type EstadoSync = 'cumple' | 'en_proceso' | 'pendiente';

export interface FilaSync {
  company_id: string;
  item_id: string;
  estado: EstadoSync;
  auto_source: string;
  updated_at: string;
}

/** Estado que decide el módulo para un ítem. */
export function estadoDelModulo(cumple: boolean, conDatos: boolean): EstadoSync {
  if (cumple) return 'cumple';
  return conDatos ? 'en_proceso' : 'pendiente';
}

/**
 * Filas a escribir: solo los ítems cuyo estado guardado (sin fila = pendiente)
 * no es el que decide el módulo. Un ítem en "No aplica" no se toca.
 */
export function filasSync(
  companyId: string,
  items: string[],
  cumpleActual: string[],
  conDatos: string[],
  existentes: EstadoGuardado[],
  fuente: string,
  ahora: string = new Date().toISOString(),
): FilaSync[] {
  const porItem = new Map(existentes.map(e => [e.item_id, e]));
  const filas: FilaSync[] = [];
  for (const itemId of items) {
    const actual = porItem.get(itemId);
    if (actual?.estado === 'no_aplica') continue;
    const deseado = estadoDelModulo(cumpleActual.includes(itemId), conDatos.includes(itemId));
    if ((actual?.estado ?? 'pendiente') === deseado) continue;
    filas.push({ company_id: companyId, item_id: itemId, estado: deseado, auto_source: fuente, updated_at: ahora });
  }
  return filas;
}

/**
 * Incidentes: qué ítems tienen datos. PO-07 y PO-08 con cualquier incidente;
 * PO-09 y PO-10 con accidentes o incidentes; PO-12 y PO-13 con enfermedades profesionales.
 */
export function itemsConDatosIncidentes(incidentes: { incident_type: string }[]): string[] {
  const items: string[] = [];
  if (incidentes.length > 0) items.push('PO-07', 'PO-08');
  if (incidentes.some(i => i.incident_type !== 'enfermedad_profesional')) items.push('PO-09', 'PO-10');
  if (incidentes.some(i => i.incident_type === 'enfermedad_profesional')) items.push('PO-12', 'PO-13');
  return items;
}

/**
 * GA-02: estado a escribir o null (nada que hacer). Con datos = hay algún
 * documento GA-02 vigente (aunque esté vencido o lo haya dejado el generador del RHS).
 */
export function decisionGa02(existente: EstadoGuardado | null | undefined, cumple: boolean, conDatos: boolean): EstadoSync | null {
  if (existente?.estado === 'no_aplica') return null;
  const deseado = estadoDelModulo(cumple, conDatos);
  return (existente?.estado ?? 'pendiente') === deseado ? null : deseado;
}
